import React, { useState, useEffect, useMemo } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormControl from "@material-ui/core/FormControl";
import FormLabel from "@material-ui/core/FormLabel";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  CircularProgress,
  Hidden,
  IconButton,
  TextField,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { toString } from "lodash";
import CloseIcon from "@material-ui/icons/Close";

import useSurveys from "../state/hooks/useSurveys";
import SurveyConfetti from "../components/SurveyConfetti";
import { CURRENT_DATE } from "../state/SurveysState";
import { paseDateWithoutTimes } from "../../../util/UtilDates";

export const BACKDROP = {
  opacity: "1",
  transition: "opacity 225ms cubic-bezier(0.4, 0, 0.2, 1)",
  background: "rgb(167 163 163 / 40%)",
  boxShadow: "0 4px 30px rgba(0, 0, 0, 0.1)",
  backdropFilter: "blur(3px)",
  WebkitBackdropFilter: "blur(5.6px)",
  border: "1px solid rgba(255, 255, 255, 0.3)",
};

const GLASS_BLUR = 2;
const GLASS = {
  background: "rgba(255, 255, 255, 0.83)",
  borderRadius: "16px",
  boxShadow: "0 4px 30px rgba(0, 0, 0, 0.1)",
  backdropFilter: `blur(${GLASS_BLUR}px)`,
  WebkitBackdropFilter: `blur(${GLASS_BLUR}px)`,
  border: "1px solid rgba(255, 255, 255, 0.3)",
};

const COLOR_IN_GLASS = "#383838";

const USE_GLASS = false;

const useStyles = makeStyles((theme) => ({
  dialogBackdrop: {
    ...BACKDROP,
  },
  dialogPaper: {
    minWidth: "650px",
    maxWidth: "800px",
    backgroundColor: "transparent",
    borderRadius: 10,
    [theme.breakpoints.down("md")]: {
      maxWidth: "90%",
      minWidth: "90%",
    },
    [theme.breakpoints.down("xs")]: {
      maxWidth: "95%",
      minWidth: "95%",
    },
  },
  dialogPaperGlassed: {
    ...GLASS,
    minWidth: "650px",
    maxWidth: "800px",
    borderRadius: 10,
  },
  dialogContent: {
    padding: theme.spacing(3),
    minHeight: 425,
    display: "flex",
    flexDirection: "column",
    background: USE_GLASS
      ? "transparent"
      : theme.palette.survey.backogroundContent,
    [theme.breakpoints.down("xs")]: {
      padding: 12,
    },
  },
  dialogContentLast: {
    padding: theme.spacing(3),
    minHeight: 425,
    display: "flex",
    flexDirection: "column",
    background: USE_GLASS
      ? "transparent"
      : theme.palette.survey.backgroundColor,
    [theme.breakpoints.down("xs")]: {
      padding: 12,
    },
  },
  dialogContentHi: {
    padding: theme.spacing(3),
    minHeight: "320px",
    display: "flex",
    flexDirection: "column",
    gap: 16,
    background: USE_GLASS
      ? "transparent"
      : theme.palette.survey.backgroundColor,
  },
  dialogContentThankYou: {
    padding: theme.spacing(4),
    minHeight: "350px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 16,
    background: USE_GLASS
      ? "transparent"
      : theme.palette.survey.backgroundColor,
  },
  handleCloseButtonTY: {
    position: "absolute",
    bottom: "8px",
    right: "8px",
    borderRadius: "8px",
    backgroundColor: "#005f8d",
    color: "white",
    fontWeight: "bold",
    "&:hover": {
      backgroundColor: "#005f8d",
      color: "white",
      fontWeight: "bold",
      transform: "scale(1.02)",
    },
    [theme.breakpoints.down("md")]: {
      left: "0px",
      right: "0px",
      width: "90%",
      bottom: "8px",
      marginInline: "auto",
    },
  },
  radioGroup: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: theme.spacing(2),
    width: "100%",
    alignItems: "center",
  },
  radioButton: {
    margin: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "20%",
  },

  solutionTitle: {
    fontWeight: "bold",
    marginBottom: theme.spacing(2),
  },
  surveyInfo: {},
  surveyCode: {
    fontWeight: "bold",
    [theme.breakpoints.down("xs")]: {
      textAlign: "left",
    },
  },
  surveyDescription: {
    marginBottom: theme.spacing(1),
  },
  surveyDate: {
    color: USE_GLASS ? COLOR_IN_GLASS : "#FFFFFF",
    textAlign: "left",
    fontStyle: "italic",
  },
  progressIndicatorText: {
    fontWeight: "bold",
    color: theme.palette.survey.progressIndicatorTextColor,
    fontSize: "1rem",
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.80rem",
    },
  },
  ratingLabel: {
    fontSize: "0.75rem",
    textAlign: "center",
    marginTop: "4px",
  },
  ratingEmoji: {
    fontSize: "1.5rem",
    marginBottom: "4px",
  },
  sopContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
  },
  sopText: {
    textAlign: "center",
    fontWeight: "bold",
  },
  solutionLogoContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-around",
    height: 250,
    flexDirection: "column",
    marginBottom: 24,
  },
  solutionLogo: {
    display: "flex",
    padding: "8px 20px",
    alignItems: "center",
    borderRadius: "12px",
    justifyContent: "center",
    maxWidth: 300,
    marginInline: "auto",
    //background: "#ffffff",
    //borderRadius: "15px",
    [theme.breakpoints.down("xs")]: {
      maxWidth: "95%",
    },
  },
  solutionLogoApo: {
    maxWidth: 400,
    //background: "#ffffff",
    //borderRadius: "15px",
    [theme.breakpoints.down("xs")]: {
      maxWidth: "95%",
    },
  },
  sopLogo: {
    maxWidth: 400,
    marginInline: "auto",
    //background: "#ffffff",
    //borderRadius: "15px",
    [theme.breakpoints.down("xs")]: {
      maxWidth: "95%",
    },
  },
  dialogTitle: {
    textAlign: "center",
    background: USE_GLASS ? "transparent" : "#3c7ee0",
    color: USE_GLASS ? COLOR_IN_GLASS : "#FFFFFF",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(1, 3),
  },
  dialogActions: {
    display: "flex",
    justifyContent: "space-between",
    padding: theme.spacing(2, 3),
    backgroundColor: USE_GLASS
      ? "transparent"
      : theme.palette.survey.backgroundColor,
  },
  closeButton: {
    color: USE_GLASS ? COLOR_IN_GLASS : "#FFFFFF",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "300px",
  },
  noSurveysContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "200px",
  },
  contentContainer: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },
  ratingsContainerBox: {
    gap: "28px",
    width: "100%",
    display: "flex",
    marginTop: "16px",
    alignItems: "center",
  },
  ratingContainer: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    borderRadius: 12,
    border: "3px solid #3463a7",
    [theme.breakpoints.down("xs")]: {
      paddingLeft: 24,
    },
  },
  ratingDescription: {
    display: "block",
    fontSize: "0.9rem",
    marginLeft: -20,
  },
  ratingHelp: {
    fontSize: "1rem",
    fontStyle: "italic",
    fontWeight: "bold",
  },
  commentBox: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    height: "250px",
  },
  sopBox: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    height: "250px",
  },
  decisionTakeSurveyBox: {
    display: "flex",
    marginTop: "24px",
    justifyContent: "center",
    alignItems: "center",
    gap: "24px",
    [theme.breakpoints.down("xs")]: {
      justifyContent: "space-between",
    },
  },
  decisionTakeSurveyBtnContinue: {
    gap: 12,

    fontWeight: "bold",
    border: "2px solid",
    "&:hover": {
      fontWeight: "bold",
      border: "2px solid",
    },
  },
  decisionTakeSurveyBtnSkip: {
    gap: 12,
    border: "2px solid",
    "&:hover": {
      border: "2px solid",
    },
  },
  summaryBox: {},
  changeRatingText: {
    fontStyle: "italic",
    marginTop: 6,
    color: theme.palette.survey.changeRatingTextColor,
    fontSize: "0.85rem",
  },
  summaryTable: {
    width: "100%",
    borderCollapse: "collapse",
  },
  summaryTableHead: {
    padding: "8px",
    borderBottom: "3px solid #000000",
    fontSize: "0.9rem",
  },
  summaryTableBody: {},
  summaryTableRow: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#3c7ee026",
    },
  },
  summaryTableCell: {
    padding: "8px",
    borderBottom: "1px solid #ddd",
  },
  loadingSendResult: {
    color: "white",
    marginLeft: 6,
  },
}));

const commonEmoji = {
  fontSize: "1.5rem",
  marginRight: 8,
  marginLeft: 8,
};

const APOLLO_SOLUTION = "apo";
const AVIS_SOLUTION = "avi";
const EMS_SOLUTION = "ems";
const FENIX_SOLUTION = "fen";
const FRIDAY_SOLUTION = "fri";
const MERCURY_SOLUTION = "mer";
const PLV_SOLUTION = "plv";
const PMT_SOLUTION = "pmt";
const TIMBO_SOLUTION = "tim";
const SOPORTE_GENERAL = "sop";

const SOLUTIONS_MAPPING_NAME = {
  [APOLLO_SOLUTION]: "SURVEYS_SOLUTION_APOLLO",
  [AVIS_SOLUTION]: "SURVEYS_SOLUTION_AVIS",
  [EMS_SOLUTION]: "SURVEYS_SOLUTION_EMS",
  [FENIX_SOLUTION]: "SURVEYS_SOLUTION_FENIX",
  [FRIDAY_SOLUTION]: "SURVEYS_SOLUTION_FRIDAY",
  [MERCURY_SOLUTION]: "SURVEYS_SOLUTION_MERCURY",
  [PLV_SOLUTION]: "SURVEYS_SOLUTION_PLV",
  [PMT_SOLUTION]: "SURVEYS_SOLUTION_PMT",
  [TIMBO_SOLUTION]: "SURVEYS_SOLUTION_TIMBO",
  [SOPORTE_GENERAL]: "SURVEYS_SOLUTION_GENERAL_SUPPORT",
};

const IS_DEVELOPMENT = process.env.NODE_ENV === "development";

const INITIAL_RATINGS = IS_DEVELOPMENT
  ? {
      [APOLLO_SOLUTION]: "5",
      [AVIS_SOLUTION]: "4",
      [EMS_SOLUTION]: "3",
      [FENIX_SOLUTION]: "2",
      [FRIDAY_SOLUTION]: "1",
      [MERCURY_SOLUTION]: "2",
      [PLV_SOLUTION]: "3",
      [PMT_SOLUTION]: "1",
      [TIMBO_SOLUTION]: "5",
      [SOPORTE_GENERAL]: "4",
    }
  : {};

const INITIAL_STEP = IS_DEVELOPMENT ? 9 : 0;

const INITIAL_COMMENT = IS_DEVELOPMENT ? "COMENTARIO DEV" : "";

export default function SurveysDialog() {
  const classes = useStyles();
  const { t } = useTranslation();
  const {
    loadingSurverys,
    surveys,
    showSurveysDialog,
    closeSurveyDialog,
    continuePressed,
    continueSurvey,
    skipSurvey,
    skipPressed,
    skipPressedDay,
    handleSendSurveyResults,
    loadingSendResults,
    showThankYou,
    resetSurveyStore,
    closedSurveyInMiddle,
  } = useSurveys();

  const [activeStep, setActiveStep] = useState(INITIAL_STEP);
  const [ratings, setRatings] = useState(INITIAL_RATINGS);
  const [currentSurvey, setCurrentSurvey] = useState(null);
  const [comment, setComment] = useState(INITIAL_COMMENT);

  // Props to display
  const currentSurveyCode = currentSurvey?.surveyCode || "-";
  const currentSurveyDecription = currentSurvey?.surveyDescription || "-";
  const currentSurveyLastDateToComplete = paseDateWithoutTimes(
    "yyyy-MM-dd",
    "dd/MM/yyyy",
    currentSurvey?.lastDateToComplete || "-"
  );
  // Solutions + comentario + summary
  const stepsSize = currentSurvey?.allowedSolutions?.length + 2 || 0;
  const currentSolutionStep = currentSurvey?.allowedSolutions?.[activeStep];

  const isSopStep = activeStep === stepsSize - 3;
  const isCommentStep = activeStep === stepsSize - 2;
  const isLastStep = activeStep === stepsSize - 1;

  useEffect(() => {
    if (surveys && surveys.length > 0 && showSurveysDialog) {
      setCurrentSurvey(surveys[0]);

      // Only overwrite ratings in non-development environments
      if (!IS_DEVELOPMENT) {
        const initialRatings = {};
        surveys.forEach((survey) => {
          survey.allowedSolutions.forEach((solution) => {
            initialRatings[solution] = ratings[solution] || "";
          });
        });
        setRatings(initialRatings);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSurveysDialog, surveys]);

  const handleRatingChange = (solution, value) => {
    setRatings({
      ...ratings,
      [solution]: value,
    });
  };

  const handleNext = async () => {
    if (isLastStep) {
      await handleSendSurveyResults({
        ratings,
        comment,
        surveyCode: currentSurveyCode,
      });
      return;
    }

    if (activeStep < stepsSize - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const handleClose = (reset) => {
    if (reset === true) {
      resetSurveyStore();
    } else {
      closeSurveyDialog();
    }
    setActiveStep(0);
    setRatings({});
  };

  const handleComment = (event) => {
    setComment(event.target.value);
  };

  const openState = useMemo(() => {
    return (
      showSurveysDialog &&
      !loadingSurverys &&
      surveys &&
      surveys.length > 0 &&
      (!skipPressed || CURRENT_DATE !== skipPressedDay) &&
      (!closedSurveyInMiddle || CURRENT_DATE !== skipPressedDay)
    );
  }, [
    closedSurveyInMiddle,
    loadingSurverys,
    showSurveysDialog,
    skipPressed,
    skipPressedDay,
    surveys,
  ]);

  return (
    <Dialog
      open={openState}
      classes={{
        paper: USE_GLASS ? classes.dialogPaperGlassed : classes.dialogPaper,
        root: classes.dialogBackdrop,
      }}
      aria-labelledby="survey-dialog-title"
      hideBackdrop
    >
      <SurveyConfetti />
      {loadingSurverys ? (
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      ) : !surveys || surveys.length === 0 ? (
        <div className={classes.noSurveysContainer}>
          <Typography variant="body1">{t("No surveys available")}</Typography>
        </div>
      ) : showThankYou ? (
        <DialogContent className={classes.dialogContentThankYou}>
          <Button
            onClick={() => handleClose(true)}
            className={classes.handleCloseButtonTY}
            startIcon={<CloseIcon />}
          >
            {t("CLOSE")}
          </Button>

          <span
            role="img"
            aria-labelledby="thank-you"
            style={{ fontSize: "2rem" }}
          >
            🥳 {t("SURVEYS_THANK_YOU_TITLE")}
          </span>

          <Typography variant="h6">
            <span role="img" aria-labelledby="reading" style={commonEmoji}>
              👨‍💻
            </span>
            {t("SURVEYS_THANK_YOU_SUBTITLE")}
          </Typography>

          <img
            alt={`dsuite-logo`}
            src={`${process.env.PUBLIC_URL}/assets/img/DSUITE_logo_new.webp`}
            className={classes.sopLogo}
          />
        </DialogContent>
      ) : (
        <>
          {continuePressed ? (
            <>
              <DialogTitle disableTypography className={classes.dialogTitle}>
                <Box className={classes.surveyInfo}>
                  <Typography variant="h4" className={classes.surveyCode}>
                    <strong>{currentSurveyDecription}</strong>{" "}
                    {`(${currentSurveyCode})`}
                  </Typography>

                  <Typography variant="h6" className={classes.surveyDate}>
                    {t("SURVEYS_END_DATE", {
                      date: currentSurveyLastDateToComplete,
                    })}
                  </Typography>
                </Box>
                <IconButton
                  className={classes.closeButton}
                  onClick={() => handleClose(false)}
                  aria-label="close"
                  disabled={loadingSendResults}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>

              <DialogContent
                className={
                  isCommentStep || isLastStep
                    ? classes.dialogContentLast
                    : classes.dialogContent
                }
              >
                {currentSurvey && (
                  <div className={classes.contentContainer}>
                    {activeStep < stepsSize && (
                      <>
                        {isLastStep ? (
                          <Box className={classes.summaryBox}>
                            <Typography variant="h6">
                              {t("SURVEYS_SUMMARY_TITLE")}
                            </Typography>
                            <Typography
                              variant="subtitle1"
                              className={classes.changeRatingText}
                            >
                              {t("SURVEYS_SUMMARY_SUBTITLE")}
                            </Typography>
                            <Box marginTop={2}>
                              <table className={classes.summaryTable}>
                                <thead>
                                  <tr>
                                    <th
                                      className={classes.summaryTableHead}
                                      style={{
                                        textAlign: "left",
                                      }}
                                    >
                                      {t("SURVEYS_SUMMARY_TABLE_COL_SOL")}
                                    </th>
                                    <th
                                      className={classes.summaryTableHead}
                                      style={{
                                        textAlign: "center",
                                      }}
                                    >
                                      {t("SURVEYS_SUMMARY_TABLE_COL_RATING")}
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {Object.keys(ratings).map(
                                    (solution, index) => {
                                      return (
                                        <tr
                                          key={solution}
                                          className={classes.summaryTableRow}
                                          onClick={() => {
                                            if (loadingSendResults) {
                                              return;
                                            }
                                            setActiveStep(index);
                                          }}
                                        >
                                          <td
                                            className={classes.summaryTableCell}
                                          >
                                            {t(
                                              SOLUTIONS_MAPPING_NAME[solution]
                                            )}
                                          </td>
                                          <td
                                            className={classes.summaryTableCell}
                                            style={{
                                              textAlign: "center",
                                            }}
                                          >
                                            {ratings[solution] || "-"}
                                          </td>
                                        </tr>
                                      );
                                    }
                                  )}
                                </tbody>
                              </table>
                            </Box>

                            <Box marginTop={3}>
                              <Typography variant="h6">
                                {t("SURVEYS_COMMENT")}
                              </Typography>
                              <TextField
                                fullWidth
                                variant="outlined"
                                multiline
                                minRows={4}
                                value={comment}
                                InputProps={{
                                  readOnly: true,
                                }}
                                disabled
                              />
                            </Box>
                          </Box>
                        ) : isCommentStep ? (
                          <Box className={classes.commentBox}>
                            <Typography variant="h5">
                              <span
                                role="img"
                                aria-labelledby="magic"
                                style={{ fontSize: "2rem" }}
                              >
                                🪄
                              </span>
                              {t("SURVEYS_COMMENT_DESC")}
                            </Typography>
                            <TextField
                              id="comment"
                              label=""
                              multiline
                              minRows={8}
                              variant="outlined"
                              value={comment}
                              onChange={handleComment}
                              autoFocus
                              style={{ color: "#000000" }}
                            />
                          </Box>
                        ) : (
                          <>
                            <Box className={classes.solutionLogoContainer}>
                              {isSopStep ? (
                                <Box className={classes.sopContainer}>
                                  <img
                                    alt={`dsuite-logo`}
                                    src={`${process.env.PUBLIC_URL}/assets/img/DSUITE_logo_new.webp`}
                                    className={classes.sopLogo}
                                  />

                                  <Typography
                                    variant="h5"
                                    className={classes.sopText}
                                  >
                                    <span
                                      role="img"
                                      aria-labelledby="almost-finish"
                                      style={{ fontSize: "1.5rem" }}
                                    >
                                      🏎️
                                    </span>{" "}
                                    {t("SURVEYS_SOP_TITLE")}
                                  </Typography>

                                  <Typography
                                    variant="h5"
                                    className={classes.sopText}
                                    style={{ marginTop: 12 }}
                                  >
                                    {t("SURVEYS_SOP_DESC")}
                                  </Typography>
                                </Box>
                              ) : (
                                <>
                                  <img
                                    alt={`${currentSolutionStep}-logo`}
                                    src={`${
                                      process.env.PUBLIC_URL
                                    }/assets/img/surveys/solutions/solution-${toString(
                                      currentSolutionStep
                                    )
                                      .toLowerCase()
                                      .trim()}.webp`}
                                    className={`${classes.solutionLogo} ${
                                      currentSolutionStep === APOLLO_SOLUTION &&
                                      classes.solutionLogoApo
                                    }`}
                                  />
                                </>
                              )}
                            </Box>

                            <FormControl
                              component="fieldset"
                              className={classes.ratingContainer}
                              fullWidth
                            >
                              {isSopStep ? null : (
                                <FormLabel
                                  component="legend"
                                  className={classes.ratingHelp}
                                >
                                  {t("SURVEYS_RATING_DESC", {
                                    solution: t(
                                      SOLUTIONS_MAPPING_NAME[
                                        currentSolutionStep
                                      ]
                                    ),
                                  })}
                                </FormLabel>
                              )}

                              <Box className={classes.ratingsContainerBox}>
                                <Hidden xsDown>
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    mr={1}
                                  >
                                    <span
                                      role="img"
                                      aria-labelledby="very-bad"
                                      style={{ fontSize: "2.5rem" }}
                                    >
                                      😔
                                    </span>
                                  </Box>
                                </Hidden>

                                <RadioGroup
                                  row
                                  style={{
                                    flex: 1,
                                    justifyContent: "space-between",
                                  }}
                                  value={toString(ratings[currentSolutionStep])}
                                  onChange={(e) =>
                                    handleRatingChange(
                                      currentSolutionStep,
                                      e.target.value
                                    )
                                  }
                                >
                                  <Box textAlign="center">
                                    <FormControlLabel
                                      value="1"
                                      control={<Radio color="primary" />}
                                      label="1"
                                    />
                                    <Typography
                                      className={classes.ratingDescription}
                                    >
                                      {t("SURVEYS_RATING_1")}
                                    </Typography>
                                  </Box>

                                  <Box textAlign="center">
                                    <FormControlLabel
                                      value="2"
                                      control={<Radio color="primary" />}
                                      label="2"
                                    />
                                    <Typography
                                      className={classes.ratingDescription}
                                    >
                                      {t("SURVEYS_RATING_2")}
                                    </Typography>
                                  </Box>

                                  <Box textAlign="center">
                                    <FormControlLabel
                                      value="3"
                                      control={<Radio color="primary" />}
                                      label="3"
                                    />
                                    <Typography
                                      className={classes.ratingDescription}
                                    >
                                      {t("SURVEYS_RATING_3")}
                                    </Typography>
                                  </Box>

                                  <Box textAlign="center">
                                    <FormControlLabel
                                      value="4"
                                      control={<Radio color="primary" />}
                                      label="4"
                                    />
                                    <Typography
                                      className={classes.ratingDescription}
                                    >
                                      {t("SURVEYS_RATING_4")}
                                    </Typography>
                                  </Box>

                                  <Box textAlign="center">
                                    <FormControlLabel
                                      value="5"
                                      control={<Radio color="primary" />}
                                      label="5"
                                    />
                                    <Typography
                                      className={classes.ratingDescription}
                                    >
                                      {t("SURVEYS_RATING_5")}
                                    </Typography>
                                  </Box>
                                </RadioGroup>

                                <Hidden xsDown>
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    ml={1}
                                  >
                                    <span
                                      role="img"
                                      aria-labelledby="very-good"
                                      style={{ fontSize: "2.5rem" }}
                                    >
                                      😍
                                    </span>
                                  </Box>
                                </Hidden>
                              </Box>
                            </FormControl>
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}
              </DialogContent>

              <DialogActions className={classes.dialogActions}>
                <Button
                  onClick={handleBack}
                  color="primary"
                  variant="outlined"
                  disabled={activeStep <= 0 || loadingSendResults}
                >
                  {t("FORM_BACK")}
                </Button>

                <Typography
                  variant="body2"
                  className={classes.progressIndicatorText}
                >
                  {t("SURVEYS_STEPS_COUNT", {
                    step: activeStep + 1,
                    stepsSize,
                  })}
                </Typography>

                <Button
                  onClick={handleNext}
                  color="primary"
                  variant="contained"
                  disabled={
                    (!isLastStep &&
                      !isSopStep &&
                      !isCommentStep &&
                      !ratings[currentSurvey?.allowedSolutions[activeStep]]) ||
                    (isSopStep && !ratings["sop"]) ||
                    loadingSendResults
                  }
                >
                  {isLastStep ? t("SURVEYS_FINISH") : t("NEXT")}
                  {isLastStep && loadingSendResults ? (
                    <CircularProgress
                      size={21}
                      className={classes.loadingSendResult}
                    />
                  ) : null}
                </Button>
              </DialogActions>
            </>
          ) : (
            <DialogContent className={classes.dialogContentHi}>
              <span
                role="img"
                aria-labelledby="hi"
                style={{ fontSize: "2rem" }}
              >
                👋 {t("SURVEYS_HI_TITLE")}
              </span>
              <Typography variant="h6">
                <span role="img" aria-labelledby="survey" style={commonEmoji}>
                  🌟
                </span>
                {t("SURVEYS_HI_SUBTITLE")}
              </Typography>
              <Typography variant="h6">
                <span role="img" aria-labelledby="watch" style={commonEmoji}>
                  ⌛
                </span>
                {t("SURVEYS_HI_ESTIMATED")}
              </Typography>

              <Typography variant="h4" color="error">
                <span role="img" aria-labelledby="warn" style={commonEmoji}>
                  🚨
                </span>
                {`¡${t("SURVEYS_END_DATE", {
                  date: currentSurveyLastDateToComplete,
                })}!`}
                <span role="img" aria-labelledby="warn-2" style={commonEmoji}>
                  🚨
                </span>
              </Typography>
              <Box className={classes.decisionTakeSurveyBox}>
                <Button
                  className={classes.decisionTakeSurveyBtnSkip}
                  variant="outlined"
                  onClick={skipSurvey}
                >
                  <span role="img" aria-labelledby="sad" style={commonEmoji}>
                    😥
                  </span>
                  {t("SURVEYS_NOT_NOW")}
                </Button>
                <Button
                  className={classes.decisionTakeSurveyBtnContinue}
                  variant="outlined"
                  color="primary"
                  onClick={continueSurvey}
                >
                  <span
                    role="img"
                    aria-labelledby="applause"
                    style={commonEmoji}
                  >
                    👏
                  </span>
                  {t("SURVEYS_CONTINUE")}
                </Button>
              </Box>
            </DialogContent>
          )}
        </>
      )}
    </Dialog>
  );
}
